import React, { useState, useEffect } from "react"
import { PlanVariantSwitch } from "app/shared/components/plan-variant-switch"
import { StripeForm } from "app/shared/components/card-form/stripe-form"
import { Elements } from "@stripe/react-stripe-js"
import { useStripe } from "hooks/use-stripe"
import { usePromoCode } from "hooks/use-promo-code"

import { Button } from "@kit/design/button"
import { Note } from "@kit/design/note"

import { BillingNotice } from "./billing-notice"

const noop = () => {}

const Discount = ({ promoCode }) => {
  const { isLaunchOfferApplied, removePromoCode } = usePromoCode()

  // The user never entered an auto-applied code, so don't tell them it failed.
  if (!promoCode.valid && isLaunchOfferApplied) {
    return null
  }

  if (promoCode.valid) {
    return (
      <Note variant="success" className="mb-8">
        <Note.Icon />
        <Note.Content>
          <Note.Heading level={3}>Discount applied!</Note.Heading>
          {promoCode.message && <p>{promoCode.message}</p>}
        </Note.Content>
      </Note>
    )
  } else {
    return (
      <Note variant="error" className="mb-8">
        <Note.Icon />
        <Note.Content>
          <Note.Heading level={3}>
            {promoCode.redemption_code} cannot be applied
          </Note.Heading>
          {promoCode.message && <p>{promoCode.message}</p>}
        </Note.Content>
        <Note.Actions>
          <Note.Dismiss
            onDismiss={removePromoCode}
            render={<Button size="small">Dismiss</Button>}
          />
        </Note.Actions>
      </Note>
    )
  }
}

export function Content({
  handleSubscription,
  stripeData,
  currentPlans = [],
  trialPeriodEnd,
  trialing,
  billingCycle,
  currentlyOnFreePlan,
  onPlanChange = noop,
  modalOptions,
  options
}) {
  const prioritizedPlan = currentPlans.find(plan => plan.prioritize_plan)
  const [activePlan, setActivePlan] = useState(
    prioritizedPlan?.id ||
      currentPlans.find(plan => plan.interval === billingCycle)?.id
  )

  const activeCurrentPlan = currentPlans.find(plan => plan.id === activePlan)

  const [stripeOptions, setStripeOptions] = useState(stripeData.options)

  const { removePromoCode } = usePromoCode()

  useEffect(() => {
    const newOptions = stripeOptions

    if (activeCurrentPlan?.promo_code?.valid) {
      newOptions.promo_code = activeCurrentPlan.promo_code.redemption_code
    } else {
      newOptions.promo_code = null
    }

    setStripeOptions(newOptions)
  }, [activePlan])

  const handlePlanChange = planId => {
    setActivePlan(planId)
    onPlanChange(planId)
  }

  const successCallback = async () => {
    if (handleSubscription != undefined) {
      handleSubscription()
    }

    removePromoCode()
  }

  const stripe = useStripe(stripeData.stripe_key)

  // current_plans comes back empty for an account with no price at its tier
  // (PlanPresenter#exceptional_case?), and every read below assumes a plan to
  // charge. Say so rather than throwing under a page that looks like a
  // checkout. Placed after the hooks so the order stays stable.
  if (!activeCurrentPlan) {
    return (
      <Note variant="error">
        <Note.Icon />
        <Note.Content>
          <Note.Heading level={3}>Checkout unavailable</Note.Heading>
          <p>
            We couldn&apos;t load a plan for your account. Get in touch and
            we&apos;ll sort it out.
          </p>
        </Note.Content>
      </Note>
    )
  }

  return (
    <div>
      {activeCurrentPlan.promo_code && (
        <Discount promoCode={activeCurrentPlan.promo_code} />
      )}

      <div className="mb-10">
        <PlanVariantSwitch
          handlePlanChange={handlePlanChange}
          activePlanId={activePlan}
          availablePlans={currentPlans}
        />
      </div>

      <div>
        <Elements stripe={stripe}>
          <StripeForm
            options={stripeOptions}
            planId={activePlan}
            successCallback={successCallback}
            modalOptions={modalOptions}
            buttonText={options?.buttonText}
            config={{
              useTooltipForDisabled: options?.buttonUsesTooltipForDisabled
            }}
          />
        </Elements>
      </div>

      <div className="mt-10">
        <BillingNotice
          plan={activeCurrentPlan}
          trialing={trialing}
          trialPeriodEnd={trialPeriodEnd}
          currentlyOnFreePlan={currentlyOnFreePlan}
          showSubscriberCount={options?.showSubscriberCountInNotice}
        />
      </div>
    </div>
  )
}
